import type {
  AccountActivity as AccountActivityRes,
  AccountInternalMap,
  AccountOwnerData,
  AccountOwnerMetadata,
  ArchiveCrbRequest,
  ArchiveCrbResponse,
  AuditTrailEvent,
  AuditTrailEventResponse,
  AutoGenReview,
  AutoGenReviewRequest,
  Bank,
  BankDispensary,
  BankDispensaryMetadata,
  BankInternalTransactionCodes,
  BankInternalTransactionCodesRequest,
  BankLastIngestionInfo,
  BankLocation,
  BankOverview,
  BankPeerGroups,
  BannerStatus,
  CoreTransactionUploadAction,
  Counterparty,
  CounterpartyActivity,
  CounterpartyMapWithLinks,
  DailySummary,
  Deposit,
  Dispensary,
  DispensaryAccountReview,
  DispensaryTransactionFromBankCore,
  DueDiligenceStatus,
  Effect,
  FincenReportTableDataResponse,
  FincenSarReport,
  FullMUO,
  FullMUOBankResponse,
  GcdAccountOwnershipRole,
  GcdUserRole,
  GetJourneysResponse,
  GrantGcdAccessRequest,
  Group,
  IngestionReportResponse,
  InheritDocumentsMap,
  Invitation,
  InviteNewDispensaryForBankRequest,
  InviteNewDispensaryForBankResponse,
  InviteNewUserRequest,
  MarketplaceLead,
  MarketplaceLeadStatus,
  MarketplaceMetadata,
  MarketplaceServiceProviderOffer,
  MiddeskBusinessResults,
  MinifiedDispensary,
  MUO,
  MuoAccountReview,
  MUOBankResponse,
  OnboardingDocumentRequirementResult,
  PayqwickUserRole,
  PlaidBankAccountsSummary,
  Post,
  RunJourneyRequest,
  RunJourneyResponse,
  RunStrategyRequest,
  RunStrategyResponse,
  SarPeriodAggregate,
  SarTableData,
  SeedlingWizardRequest,
  Task,
  TemplateResultId,
  TemplateResultResponse,
  UnarchiveCrbRequest,
  UnarchiveCrbResponse,
  UpdateDispensaryInternalIdResponse,
  User
} from '@gcv/shared';
import { injectable } from 'inversify';

import type { GroupPermissions, UserPermissions } from 'domain/types/permissions';
import type { DepositTableExtended } from 'ui/apps/fi/deposits/deposits.repo';
import { api, banksApi } from './api-util/api';
import type { ApiResponse } from './api-util/api-response';

@injectable()
export class BanksApi {
  async getBankById(bankId: string): Promise<Bank> {
    return await banksApi().get(`/banks/${bankId}`);
  }

  async getBanks(): Promise<Bank[]> {
    return await banksApi().get('/banks');
  }

  async getBankStaff(bankId: string): Promise<User[]> {
    return await api().get(`/organizations/${bankId}/users`);
  }

  async inviteBankUser(bankId: string, data: InviteNewUserRequest): Promise<{ org: Bank; user: User }> {
    return await banksApi().post(`/banks/${bankId}/invite-user`, data);
  }

  async getBankOverview(
    bankId: string,
    startDate: string,
    endDate: string,
    snowflake: boolean
  ): Promise<BankOverview> {
    return await banksApi().get(`/banks/${bankId}/overview`, {
      startDate,
      endDate,
      snowflake
    });
  }

  async getBankDispensary(bankId: string, dispId: string): Promise<MUO | BankDispensary> {
    return await banksApi().get(`/banks/${bankId}/dispensaries/${dispId}`);
  }

  async getMinifiedDispensaries(bankId: string): Promise<MinifiedDispensary[]> {
    return await banksApi().get(`/banks/${bankId}/minified-dispensaries`);
  }

  async getInvitedDispensaries(bankId: string): Promise<Invitation[]> {
    return await banksApi().get(`/banks/${bankId}/invited-crbs`);
  }

  async getDailySummaries(
    bankId: string,
    startDate: string,
    endDate: string,
    snowflake: boolean
  ): Promise<DailySummary[]> {
    return await banksApi().get(`/banks/${bankId}/dailySummaryTable`, { startDate, endDate, snowflake });
  }

  async createBank(orgName: string, state: string, country: string, dispensaries: string[]): Promise<Bank> {
    return await banksApi().post('/banks', { orgName, state, country, dispensaries });
  }

  async assignDispensaryToBank(bankId: string, dispId: string, templateId: string): Promise<Bank> {
    return await banksApi().put(`/banks/${bankId}/dispensaries/${dispId}`, { template_id: templateId });
  }

  async addDispensaryToBank(
    bankId: string,
    body: InviteNewDispensaryForBankRequest
  ): Promise<InviteNewDispensaryForBankResponse | { data: any }> {
    return await banksApi().post(`/banks/${bankId}/dispensaries`, body);
  }

  async removeDispensaryFromBank(bankId: string, dispId: string): Promise<Bank> {
    return await banksApi().delete(`/banks/${bankId}/dispensaries/${dispId}`);
  }

  async updateBankGroups(orgId: string, groups: Group[]): Promise<Bank> {
    return await api().put(`/groups/${orgId}`, groups);
  }

  async upsertBankTheme(
    bankId: string,
    theme: { footer_html?: string; logo_s3_key?: string }
  ): Promise<Bank> {
    return await banksApi().post(`/banks/${bankId}/theme`, { theme });
  }

  async getBankInternalCodes(bankId: string): Promise<BankInternalTransactionCodes> {
    return await banksApi().get(`/banks/${bankId}/internal-transaction-codes`);
  }

  async getAccountActivity(
    bankId: string,
    dispId: string,
    startDate: string,
    endDate: string,
    accountNumbers: string[],
    locationIds: string[],
    batchSize: string,
    snowflake: boolean
  ): Promise<AccountActivityRes> {
    return await banksApi().post(
      `/banks/${bankId}/dispensaries/${dispId}/activity?startDate=${startDate}&endDate=${endDate}&batchSize=${batchSize}`,
      { accountNumbers, locationIds },
      { snowflake }
    );
  }

  async getBankPeerGroups(bankId: string): Promise<BankPeerGroups> {
    return await banksApi().get(`/banks/${bankId}/peer-groups`);
  }

  async createBankPeerGroup(bankId: string, body: Record<string, any>): Promise<BankPeerGroups> {
    return await banksApi().post(`/banks/${bankId}/peer-groups`, body);
  }

  async updatePeergroupDispensaries(
    bankId: string,
    peerGroupId: string,
    body: Record<string, any>
  ): Promise<BankPeerGroups> {
    return await banksApi().post(`/banks/${bankId}/peer-groups/${peerGroupId}/dispensaries`, body);
  }

  async updateBankPeerGroup(
    bankId: string,
    peerGroupId: string,
    body: Record<string, any>
  ): Promise<BankPeerGroups> {
    return await banksApi().put(`/banks/${bankId}/peer-groups/${peerGroupId}`, body);
  }

  async addDispensaryToPeerGroup(
    bankId: string,
    peerGroupId: string,
    dispensaryId: string
  ): Promise<BankPeerGroups> {
    return await banksApi().post(
      `/banks/${bankId}/peer-groups/${peerGroupId}/dispensary/${dispensaryId}`,
      {}
    );
  }

  async updateBankOrganizationalDetails(
    bankId: string,
    {
      /**
       * Organization
       */
      orgName,
      certificateOrCharterNumber,
      rssdNumber,
      primaryReg,
      /**
       * Contact Information
       */
      phoneNumber,
      website,
      /**
       * Hours of Operation
       */
      businessHours,
      /**
       * Main Office
       */
      address,
      city,
      state,
      postalCode,
      country,
      /**
       * Regulatory Reporting Organization
       */
      transmitter_control_code,
      regulatorContactOffice,
      ein,
      primaryRegulatorPhone
    }: Pick<
      Bank,
      | 'orgName'
      | 'certificateOrCharterNumber'
      | 'rssdNumber'
      | 'primaryReg'
      | 'phoneNumber'
      | 'website'
      | 'businessHours'
      | 'address'
      | 'city'
      | 'state'
      | 'postalCode'
      | 'country'
      | 'transmitter_control_code'
      | 'ein'
      | 'regulatorContactOffice'
      | 'primaryRegulatorPhone'
    >
  ) {
    /**
     * Clearing just a few props so as to not allow all values to be send.
     */
    return await banksApi().put(`/banks/${bankId}/organizational-details`, {
      orgName,
      certificateOrCharterNumber,
      rssdNumber,
      primaryReg,
      phoneNumber,
      website,
      businessHours,
      address,
      city,
      state,
      postalCode,
      country,
      regulatorContactOffice,
      ein,
      primaryRegulatorPhone,
      transmitter_control_code
    });
  }

  async updateBankCannabisProgram(
    bankId: string,
    data: { primary_point_of_contact: Exclude<Bank['primary_point_of_contact'], undefined> }
  ) {
    return await banksApi().put(`/banks/${bankId}/cannabis-program`, data);
  }

  async addTransportVendor(bankId: string, body: Record<string, any>) {
    return await banksApi().post(`/banks/${bankId}/transportVendor`, body);
  }

  async updateOperationalDetails(
    bankId: string,
    dispensaryId: string,
    data: {
      businessHours?: any;
      monthlySales?: number;
      monthlyCustomers?: string;
      pos_name?: string;
      ftEmployees?: string;
      ptEmployees?: string;
      business_type?: 'retail' | 'wholesale';
    }
  ): Promise<BankDispensary> {
    return await banksApi().put(`/banks/${bankId}/crb/${dispensaryId}/operational-details`, data);
  }

  async getMuo(bankId: string): Promise<MUOBankResponse[]> {
    return await banksApi().get(`/banks/${bankId}/muo`);
  }

  async addLocation(
    bankId: string,
    body: InviteNewDispensaryForBankRequest
  ): Promise<InviteNewDispensaryForBankResponse> {
    return await banksApi().post(`/banks/${bankId}/dispensaries`, body);
  }

  async switchDDTemplate(
    bankId: string,
    crbId: string,
    template_id: string,
    inheritDocumentsMap: InheritDocumentsMap,
    status: DueDiligenceStatus
  ): Promise<MinifiedDispensary> {
    return await banksApi().put(`/banks/${bankId}/crbs/${crbId}/switch-onboarding-template`, {
      template_id,
      inheritDocumentsMap,
      status
    });
  }

  async getFincenReports(bankId: string): Promise<FincenReportTableDataResponse> {
    return await banksApi().get(`/banks/${bankId}/fincen-reports`);
  }

  async getUpcomingFincenReports(bankId: string): Promise<SarPeriodAggregate[]> {
    return await api().get(`/fincen-reports/sar-periods/${bankId}`);
  }

  async updateDispensaryNickname(bankId: string, dispId: string, nickname: string): Promise<BankDispensary> {
    return await banksApi().put(`/banks/${bankId}/dispensaries/${dispId}/nickname`, { nickname });
  }

  async updateBusinessDetails(
    bankId: string,
    dispId: string,
    body: Record<string, any>
  ): Promise<BankDispensary> {
    return await banksApi().put(`/banks/${bankId}/dispensaries/${dispId}/business-details`, body);
  }

  async createLocation(
    bankId: string,
    dispId: string,
    body: Record<string, any>
  ): Promise<FullMUOBankResponse> {
    return await banksApi().put(`/banks/${bankId}/dispensaries/${dispId}/convert-to-muo`, body);
  }

  async removeLocationFromParent(
    bankId: string,
    muoId: string,
    dispId: string
  ): Promise<FullMUOBankResponse> {
    return await banksApi().delete(`/banks/${bankId}/muo/${muoId}/locations/${dispId}`);
  }

  async getDispensaryUpcomingReports(
    bankId: string,
    crbId: string
  ): Promise<{
    finCenReports: Pick<FincenSarReport, 'id' | 'due_date'>[];
    accountMonitoringReviews: Pick<Task, 'id' | 'process_date'>[];
  }> {
    return await banksApi().get(`/banks/${bankId}/crb/${crbId}/upcoming-reports`);
  }

  async archiveMuo(bankId: string, muoId: string): Promise<FullMUOBankResponse> {
    return await banksApi().delete(`/banks/${bankId}/muo/${muoId}`);
  }

  async archiveCrb(bankId: string, crbId: string, request: ArchiveCrbRequest): Promise<ArchiveCrbResponse> {
    return await banksApi().put(`/banks/${bankId}/crb/${crbId}/archive`, request);
  }

  async unarchiveCrb(
    bankId: string,
    crbId: string,
    data: UnarchiveCrbRequest
  ): Promise<UnarchiveCrbResponse> {
    return await banksApi().put(`/banks/${bankId}/crb/${crbId}/unarchive`, data);
  }

  async setToAwaitingReview(
    bankId: string,
    dispId: string,
    templateResultId: TemplateResultId
  ): Promise<FullMUO> {
    return await banksApi().put(
      `/banks/${bankId}/dispensaries/${dispId}/template-results/${templateResultId}/set-awaiting-review`,
      {}
    );
  }

  async associateParent(
    bankId: string,
    muoId: string,
    body: { dispensary_id: string; merge_autogen_reviews: boolean }
  ): Promise<{
    muo: MUO;
    dispensaries: Dispensary[]; // todo the UI won't need this in the future
    muoMetdata: BankDispensaryMetadata;
    dispensaryMetadata: BankDispensaryMetadata;
  }> {
    return await banksApi().post(`/banks/${bankId}/muo/${muoId}/locations`, body);
  }

  async createMuoReview(
    bankId: string,
    muoId: string,
    body: Record<string, any>,
    snowflake: boolean,
    v2?: boolean
  ): Promise<MuoAccountReview> {
    return await banksApi().post(`/banks/${bankId}/muo/${muoId}/review?v2=${!!v2}`, body, { snowflake });
  }

  async createDispensaryReview(
    bankId: string,
    dispId: string,
    body: Record<string, any>,
    foreground: boolean,
    snowflake: boolean,
    v2?: boolean
  ): Promise<Partial<DispensaryAccountReview>> {
    return await banksApi().post(
      `/banks/${bankId}/dispensaries/${dispId}/review?v2=${!!v2}&foreground=${foreground}`,
      body,
      { snowflake }
    );
  }

  async addDefaultTransportVendor(bankId: string, vendorId: string, body: Record<string, any>): Promise<any> {
    return await banksApi().put(`/banks/${bankId}/transportVendor/${vendorId}/dispensaries`, body);
  }

  async setFavoriteTransportVendor(
    bankId: string,
    vendorId: string,
    dispensaryId: string
  ): Promise<BankDispensary> {
    return await banksApi().put(
      `/banks/${bankId}/transportVendor/${vendorId}/dispensaries/${dispensaryId}`,
      {}
    );
  }

  async removeFavoriteFromTransportVendor(
    bankId: string,
    vendorId: string,
    dispensaryId: string
  ): Promise<BankDispensary> {
    return await banksApi().delete(
      `/banks/${bankId}/transportVendor/${vendorId}/dispensaries/${dispensaryId}`
    );
  }

  async updateTransportVendor(bankId: string, vendorId: string, body: Record<string, any>): Promise<any> {
    return await banksApi().put(`/banks/${bankId}/transportVendor/${vendorId}`, body);
  }

  async getDepositsTable(
    bankId: string,
    startDate: string,
    endDate: string,
    filterType?: string
  ): Promise<DepositTableExtended[]> {
    filterType = filterType || 'date_created';
    return await banksApi().get(
      `/banks/${bankId}/depositsTable?startDate=${startDate}&endDate=${endDate}&filter=${filterType}`
    );
  }

  async upsertBankTransactionCodes(bankId: string, codes: BankInternalTransactionCodesRequest) {
    return await banksApi().post(`/banks/${bankId}/internal-transaction-codes`, { internal_codes: codes });
  }

  async getDepositById(bankId: string, depositId: string): Promise<Deposit> {
    return await banksApi().get(`/banks/${bankId}/deposits/${depositId}`);
  }

  async reconcileDeposit(bankId: string, deposit: Deposit): Promise<Deposit> {
    return await banksApi().post(
      `/banks/${bankId}/dispensaries/${deposit.dispensary_id}/deposits/${deposit.deposit_id}/accept`,
      deposit
    );
  }

  async editDepositStatus(bankId: string, deposit: Deposit, status: string, date: string): Promise<Deposit> {
    status = status === 'under_review' ? 'under-review' : 'pending';
    return await banksApi().post(
      `/banks/${bankId}/dispensaries/${deposit.dispensary_id}/deposits/${deposit.deposit_id}/${status}`,
      {
        reviewStartDate: date
      }
    );
  }

  async completeCoreTransactionUpload(
    bankId: string,
    key: string,
    bucket: string,
    action: CoreTransactionUploadAction
  ): Promise<Deposit> {
    return await banksApi().post(`/banks/${bankId}/complete-core-transaction-upload`, {
      key,
      bucket,
      action
    });
  }

  async deleteBank(bankId: string): Promise<null> {
    return await banksApi().delete(`/banks/${bankId}`);
  }

  /* Old endpoint for single ID */
  async updateDispensaryInternalId(
    bankId: string,
    dispensaryId: string,
    internalId: string,
    dryRun: boolean
  ): Promise<UpdateDispensaryInternalIdResponse> {
    return await banksApi().put(
      `/banks/${bankId}/dispensary/${dispensaryId}/internalId`,
      {
        internal_id: internalId
      },
      { dryRun }
    );
  }

  /* New endpoint for multiple ID's */
  async updateDispensaryBankAccounts(
    bankId: string,
    dispensaryId: string,
    accounts: AccountInternalMap,
    dryRun: boolean
  ): Promise<UpdateDispensaryInternalIdResponse> {
    return await banksApi().put(
      `/banks/${bankId}/dispensary/${dispensaryId}/internalId`,
      {
        accounts: accounts
      },
      { dryRun }
    );
  }

  async renameDocumentFriendlyName(
    bankId: string,
    crbId: string,
    templateId: string,
    requirementId: string,
    body: Record<string, any>
  ): Promise<OnboardingDocumentRequirementResult> {
    return await banksApi().put(
      `/banks/${bankId}/crbs/${crbId}/onboarding-templates/${templateId}/requirements/${requirementId}/friendly-filenames`,
      body
    );
  }

  async addLocations(bankId: string, locations: BankLocation[]): Promise<Bank> {
    return await banksApi().put(`/banks/${bankId}/add-locations`, locations);
  }

  async updateAutoGenReviews(
    bankId: string,
    dispensaryId: string,
    auto_gen_reviews: AutoGenReviewRequest[]
  ): Promise<AutoGenReview[]> {
    return await banksApi().put(`/banks/${bankId}/dispensaries/${dispensaryId}/autoGenReviews`, {
      auto_gen_reviews: auto_gen_reviews
    });
  }

  async getDispensaryLastIngestion(bankID: string, crbID: string): Promise<BankLastIngestionInfo> {
    return await banksApi().get(`/banks/${bankID}/dispensaries/${crbID}/lastSync`);
  }

  async getAutoGenReviews(bankId: string, dispensaryId: string): Promise<AutoGenReview[]> {
    return await banksApi().get(`/banks/${bankId}/dispensaries/${dispensaryId}/autoGenReviews`);
  }

  async deleteAutoGenReview(bankId: string, dispensaryId: string, scheduleId: string): Promise<void> {
    await banksApi().delete(
      `/banks/${bankId}/dispensaries/${dispensaryId}/schedule/${scheduleId}/autoGenReviews`
    );
  }

  async getDispensaryIngestionData(
    bankID: string,
    dispId: string,
    startDate: string,
    endDate: string
  ): Promise<IngestionReportResponse[]> {
    return await banksApi().get(
      `/banks/${bankID}/dispensaries/${dispId}/ingestion-reports?startDate=${startDate}&endDate=${endDate}`
    );
  }

  async getPostForBank(bankId: string, postId: string): Promise<Post> {
    return await banksApi().get(`/banks/${bankId}/post/${postId}`);
  }

  /**
   * Approve a CRB user and grants access to PQ. Use must not already be created in PQ and must have a role defined
   * @param bankId Bank ID
   * @param userId ID of the CRB user to approve in PQ
   * @param eid Entity ID
   * @returns Updated User record
   */
  async approvePayqwickUser(bankId: string, userId: string, eid: string): Promise<User> {
    return await banksApi().post(`/banks/${bankId}/users/${userId}/approve-payqwick`, { eid });
  }

  /**
   * Approve a CRB user and grants access to PQ. Can be used to add user to additional orgs
   * @param bankId Bank ID
   * @param userId ID of the CRB user to approve in PQ
   * @param eid Entity ID
   * @returns Updated User record
   */
  async grantPayqwickAccess(
    bankId: string,
    crbId: string,
    userId: string,
    eid: string,
    role: PayqwickUserRole
  ): Promise<User> {
    return await banksApi().post(`/banks/${bankId}/crb/${crbId}/user/${userId}/grant-pq-access`, {
      eid,
      role
    });
  }

  /**
   * Approve a CRB user and grants access to GCD. Can be used to add user to additional orgs
   * @param bankId Bank ID
   * @param userId ID of the CRB user to approve in GCD
   * @param eid Entity ID
   * @returns Updated User record
   */
  async grantGcdAccess(
    bankId: string,
    crbId: string,
    userId: string,
    role: GcdUserRole,
    account_accesses: {
      accountId: string;
      ownershipRole: GcdAccountOwnershipRole;
    }[]
  ): Promise<User> {
    const request: GrantGcdAccessRequest = { role, account_accesses };
    return await banksApi().post(`/banks/${bankId}/crb/${crbId}/user/${userId}/grant-gcd-access`, request);
  }

  async getCrbOnboardingApplicationSnapshotPdf(bankId: string, crbId: string): Promise<{ s3Link: string }> {
    return await banksApi().get(`/banks/${bankId}/crb/${crbId}/application-snapshot-pdf`);
  }

  async getCrbOnboardingLiveApplicationSnapshotPdf(
    bankId: string,
    crbId: string
  ): Promise<{ s3Link: string }> {
    return await banksApi().get(`/banks/${bankId}/crb/${crbId}/live-application-snapshot-pdf`);
  }

  async reingestDispensarySales(
    bankId: string,
    crbId: string,
    dates: { start_date: string; end_date: string }
  ): Promise<void> {
    return await banksApi().post(`/banks/${bankId}/crb/${crbId}/ingest-sales`, dates);
  }

  async getAccountOwnerMetadata(bankId: string): Promise<AccountOwnerMetadata> {
    return await banksApi().get(`/banks/${bankId}/metadata/account-owners`);
  }

  async updateAccountOwnerMetadata(bankId: string, data: AccountOwnerData): Promise<AccountOwnerMetadata> {
    return await banksApi().put(`/banks/${bankId}/metadata/account-owners`, data);
  }

  async updateCrbRelationshipManager(
    bankId: string,
    crbId: string,
    data: {
      assigned_user: string | null;
      assigned_group: string | null;
    }
  ): Promise<AccountOwnerMetadata> {
    return await banksApi().put(`/banks/${bankId}/crb/${crbId}/relationship-manager`, data);
  }

  async getUsersWithPermissions(bankId: string): Promise<UserPermissions> {
    return await banksApi().get(`/banks/${bankId}/users/permissions`);
  }

  async getGroupsWithPermissions(bankId: string): Promise<GroupPermissions> {
    return await banksApi().get(`/banks/${bankId}/groups/permissions`);
  }

  async getArchiveUserSideEffects(bankId: string, userIds: string[]): Promise<Effect[]> {
    return await banksApi().post(`/banks/${bankId}/effects/archive-user/query`, { userIds });
  }

  async executeArchiveUserSideEffects(
    bankId: string,
    userIds: string[],
    sideEffects: Effect[]
  ): Promise<void> {
    return await banksApi().post(`/banks/${bankId}/effects/archive-user/execute`, { userIds, sideEffects });
  }

  async getUpdateGroupSideEffects(bankId: string, groupId: string): Promise<Effect[]> {
    return await banksApi().post(`/banks/${bankId}/effects/update-group/query`, { groupId });
  }

  async executeUpdateGroupSideEffects(bankId: string, groupId: string, sideEffects: Effect[]): Promise<void> {
    return await banksApi().post(`/banks/${bankId}/effects/update-group/execute`, { groupId, sideEffects });
  }

  async moveDocument(
    bankId: string,
    crbId: string,
    documentId: string,
    moveFromRequirementId: string,
    moveToRequirementId: string,
    otherLocation?: 'due_diligence_template' | 'questionnaire_template'
  ): Promise<TemplateResultResponse> {
    return await banksApi().post(`/banks/${bankId}/crb/${crbId}/move-documents`, {
      documentId,
      moveFromRequirementId,
      moveToRequirementId,
      otherLocation
    });
  }

  async getAccountOwnerLastUpdatedInfo(bankId: string): Promise<{
    [appAreaName: string]: { updatedBy: string; updatedDate: string };
  }> {
    return await banksApi().get(`/banks/${bankId}/account-owners/last-updated`);
  }

  async getBankSigmaUrl(bankId: string): Promise<{ url: string }> {
    return await banksApi().get(`/banks/${bankId}/generate-sigma-url`);
  }

  async resendInviteEmail(bankId: string, inviteId: string): Promise<{ url: string }> {
    return await banksApi().post(`/banks/${bankId}/resend-invitation`, { invitationId: inviteId });
  }

  async getCompletedSarsByAccount(bankId: string, dispensaryId: string): Promise<SarTableData[]> {
    return await banksApi().get(`/banks/${bankId}/dispensary/${dispensaryId}/fincen-reports/SAR`);
  }

  async getBusinessVerification(
    bankId: string,
    crbId: string
  ): Promise<{ results: MiddeskBusinessResults | null }> {
    return await banksApi().get(`/banks/${bankId}/crb/${crbId}/business-verification`);
  }

  async createBusinessVerification(
    bankId: string,
    crbId: string
  ): Promise<{ results: MiddeskBusinessResults }> {
    return await banksApi().post(`/banks/${bankId}/crb/${crbId}/business-verification`, {});
  }

  async reverifyBusiness(bankId: string, crbId: string): Promise<{ results: MiddeskBusinessResults }> {
    return await banksApi().put(`/banks/${bankId}/crb/${crbId}/business-verification`, {});
  }

  async requestMiddeskAccess(bankId: string): Promise<{ bank: Bank; email_sent: boolean }> {
    return await banksApi().post(`/banks/${bankId}/middesk/request-access`, {});
  }

  async getBusinessVerificationPdf(
    bankId: string,
    crbId: string
  ): Promise<ApiResponse<{ data: string; type: 'pdf' | 'zip' }>> {
    return await banksApi().getVerboseResponse(`/banks/${bankId}/crb/${crbId}/business-verification-pdf`);
  }

  async getBusinessVerificationPeople(
    bankId: string,
    crbId: string
  ): Promise<{
    people: { name: string; dob?: string; availableForCriminalHistory: boolean; availableForKyc: boolean }[];
  }> {
    return await banksApi().get(`/banks/${bankId}/crb/${crbId}/business-verification-people`);
  }

  async orderAdverseMedia(bankId: string, crbId: string): Promise<{ results: MiddeskBusinessResults }> {
    return await banksApi().post(`/banks/${bankId}/crb/${crbId}/adverse-media`, {});
  }

  async orderCriminalHistory(bankId: string, crbId: string): Promise<{ results: MiddeskBusinessResults }> {
    return await banksApi().post(`/banks/${bankId}/crb/${crbId}/criminal-history`, {});
  }

  async orderKyc(
    bankId: string,
    crbId: string,
    people: { name: string; dob?: string }[]
  ): Promise<{ results: MiddeskBusinessResults }> {
    return await banksApi().post(`/banks/${bankId}/crb/${crbId}/kyc`, { people });
  }

  async getJourneys(bankId: string): Promise<GetJourneysResponse> {
    return await banksApi().get(`/banks/${bankId}/risk-narrative-journeys`);
  }

  async runJourney(bankId: string, crbId: string, request: RunJourneyRequest): Promise<RunJourneyResponse> {
    return await banksApi().post(`/banks/${bankId}/crb/${crbId}/risk-narrative-journey`, request);
  }

  async runStrategy(
    bankId: string,
    crbId: string,
    request: RunStrategyRequest
  ): Promise<RunStrategyResponse> {
    return await banksApi().post(`/banks/${bankId}/crb/${crbId}/risk-narrative-strategy`, request);
  }

  async getFiMarketplaceOffers(
    bankId: string,
    getInactiveOffers?: boolean
  ): Promise<MarketplaceServiceProviderOffer[]> {
    return await banksApi().get(
      `/banks/${bankId}/marketplace/offerings${getInactiveOffers ? '?incomplete=true' : ''}`
    );
  }

  async createMinimalMarketplaceOffer(
    bankId: string,
    request: { title: string; internal_title: string }
  ): Promise<MarketplaceServiceProviderOffer> {
    return await banksApi().post(`/banks/${bankId}/marketplace/minimal-offerings`, request);
  }

  async updateMarketplaceOffer(
    bankId: string,
    request: MarketplaceServiceProviderOffer
  ): Promise<MarketplaceMetadata> {
    return await banksApi().put(`/banks/${bankId}/marketplace/offerings/${request.id}`, request);
  }

  async activateMarketPlaceOffer(
    bankId: string,
    request: Pick<MarketplaceServiceProviderOffer, 'id' | 'status'>
  ): Promise<MarketplaceMetadata> {
    return await banksApi().put(`/banks/${bankId}/marketplace/offerings/${request.id}/activate`, request);
  }

  async getPlaidAccountsForCrb(bankId: string, crbId: string): Promise<PlaidBankAccountsSummary> {
    return await banksApi().get(`/banks/${bankId}/crb/${crbId}/plaid-accounts`);
  }

  async refreshPlaidAccountBalances(bankId: string, crbId: string): Promise<any> {
    return await banksApi().post(`/banks/${bankId}/refresh-plaid-account-balances`, { crbId });
  }

  async getMarketplaceLeads(bankId: string): Promise<MarketplaceLead[]> {
    return await banksApi().get(`/banks/${bankId}/marketplace/leads`);
  }

  async updateMarketplaceLead(
    bankId: string,
    leadId: string,
    props: { status: MarketplaceLeadStatus; internalComment?: string; externalComment?: string }
  ): Promise<MarketplaceLead> {
    return await banksApi().put(`/banks/${bankId}/marketplace/leads/${leadId}`, props);
  }

  async convertMarketplaceLead(
    bankId: string,
    leadId: string,
    props: { template_id: string }
  ): Promise<{ lead: MarketplaceLead; dispensary: BankDispensary; minifiedDispensary: MinifiedDispensary }> {
    return await banksApi().post(`/banks/${bankId}/marketplace/leads/${leadId}/convert`, props);
  }

  async updateDispensaryStage(
    bankId: string,
    dispensaryId: string,
    data: SeedlingWizardRequest
  ): Promise<Dispensary> {
    return await banksApi().post(`/banks/${bankId}/crb/${dispensaryId}/seedling-wizard`, { ...data });
  }

  async getDispensaryBannerStatus(bankId: string, crbId: string): Promise<BannerStatus[]> {
    return await banksApi().get(`/banks/${bankId}/crb/${crbId}/banner-status`);
  }

  async getBankDispensaryAuditTrailEvents(
    bankId: string,
    dispId: string
  ): Promise<AuditTrailEventResponse<AuditTrailEvent>[]> {
    return await banksApi().get(`/banks/${bankId}/crb/${dispId}/audit-trail-events`);
  }

  async getBankCounterparties(bankId: string, crbId?: string): Promise<CounterpartyMapWithLinks> {
    return await banksApi().get(`/banks/${bankId}/counterparties`, { crb_id: crbId });
  }

  async getCounterpartyActivity(
    bankId: string,
    startDate: string,
    endDate: string,
    orgId?: string
  ): Promise<CounterpartyActivity[]> {
    return await banksApi().get(`/banks/${bankId}/counterparty-activity`, { startDate, endDate, orgId });
  }

  async upsertBankCounterparties(bankId: string, counterparties: Counterparty[]): Promise<Counterparty[]> {
    return await banksApi().put(`/banks/${bankId}/counterparties`, { counterparties });
  }

  async getCrbCounterparties(
    bankId: string,
    crbIds: string[],
    includeDeleted = false
  ): Promise<{ [crbId: string]: { [counterpartyId: string]: Counterparty } }> {
    return await banksApi().get(`/banks/${bankId}/crb-counterparties`, {
      crbIds: crbIds.join(','),
      includeDeleted
    });
  }

  async getCoreTransactionsById(bankId: string, ids: string[]): Promise<DispensaryTransactionFromBankCore[]> {
    return await banksApi().get(`/banks/${bankId}/core-transactions`, { ids: ids.join(',') });
  }

  async upsertCounterpartyActivity(
    bankId: string,
    counterpartyActivity: CounterpartyActivity | CounterpartyActivity[]
  ): Promise<CounterpartyActivity[]> {
    return await banksApi().put(`/banks/${bankId}/counterparty-activity`, { counterpartyActivity });
  }
}
